type WindowYT = {
  Player?: new (element: HTMLElement, options: unknown) => { destroy?: () => void; [k: string]: unknown };
};

export type YTGlobal = typeof window & { YT?: WindowYT; onYouTubeIframeAPIReady?: () => void };

export interface YTPlayer {
  destroy?: () => void;
}

export interface YTPlayerInstance {
  loadVideoById: (id: string) => void;
  playVideo: () => void;
  mute?: () => void;
  unMute?: () => void;
  isMuted: () => boolean;
  getCurrentTime?: () => number;
  getPlayerState?: (() => number | undefined) | undefined;
  getPlaybackQuality?: () => string;
  setPlaybackQuality?: (quality: string) => void;
  pauseVideo?: () => void;
  seekTo?: (seconds: number, allowSeekAhead?: boolean) => void;
  setVolume?: (v: number) => void;
  getVolume?: () => number;
  getVideoData?: () => { author: string; title: string; video_id: string };
}

export type YTPlayerReadyEvent = { target: YTPlayerInstance };

export type YTPlayerState = -1 | 0 | 1 | 2 | 3 | 5 | 6;

export interface YTPlayerOptions {
  height: string;
  width: string;
  videoId: string;
  playerVars: {
    autoplay?: number;
    controls?: number;
    rel?: number;
    disablekb?: number;
    loop?: number;
    start?: number;
    end?: number;
    playlist?: string;
    mute?: number;
  };
  events?: {
    onReady?: (event: YTPlayerReadyEvent) => void;
  };
}

export type StateChangeEvent = { data?: number } & Record<string, unknown>

export type VideoPlayerProps = {
  videoId: string;
  volume?: number;
  options?: Partial<YTPlayerOptions>;
  className?: string;
  ariaHidden?: boolean;
  onFirstFrame?: (e: StateChangeEvent) => void;
  onStateChange?: (state: YTPlayerState) => void;
};

declare global {
  interface Window {
    YT: {
      Player: new (element: HTMLElement, options: YTPlayerOptions) => YTPlayer;
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

export type VideoPlayerHandle = {
  load: (id: string) => Promise<void>;
  play: () => Promise<void>;
  pause: () => void;
  mute: () => void;
  unmute: () => void;
  togglePlay: () => void;
  setVolume: (n: number) => void;
  isMuted: () => boolean;
  getVideoData: (() => { author: string; title: string; video_id: string } | undefined) | undefined;
  getCurrentTime: (() => number | undefined) | undefined;
  getPlayerState: (() => number);
  getVolume: (() => number | undefined);
};


let apiPromise: Promise<unknown> | null = null;
let injected = false;
const registry: Record<string, unknown> = {};
let idCounter = 0;

const initApiPromise = (): Promise<unknown> =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('No window'));
    const win = window as unknown as YTGlobal;
    if (win.YT && typeof win.YT.Player === 'function') {
      return resolve(win.YT);
    }

    const prev = win.onYouTubeIframeAPIReady;
    win.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof prev === 'function') prev();
      } catch {
        // ignore
      }
      resolve((window as unknown as YTGlobal).YT);
    };

    if (!injected) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      injected = true;
    }
  });

export const loadYouTubeApi = (): Promise<unknown> => {
  if (apiPromise) return apiPromise;
  apiPromise = initApiPromise();
  return apiPromise;
};

export const createPlayer = async (container: HTMLElement, options: unknown, requestedId?: string) => {
  await loadYouTubeApi();
  const base = requestedId || `yt-${Date.now()}`;
  let id = base;
  while (registry[id]) {
    id = `${base}-${++idCounter}`;
  }

  const win = window as unknown as YTGlobal;
  const PlayerCtor = win.YT?.Player;
  if (!PlayerCtor) throw new Error('YT.Player not available');

  const player = new PlayerCtor(container, options as unknown as Record<string, unknown>);
  registry[id] = player as unknown;
  return { id, player } as { id: string; player: unknown };
};

export const getPlayer = (id: string) => registry[id];

export const destroyPlayer = (id: string) => {
  const p = registry[id] as { destroy?: () => void; stopVideo?: () => void } | undefined;
  if (!p) return;
  try {
    if (typeof p.destroy === 'function') p.destroy();
    else if (typeof p.stopVideo === 'function') p.stopVideo();
  } catch {
    // ignore
  }
  delete registry[id];
};
